import { useState } from "react"
import { Link } from "react-router-dom"
import { useSeoMeta } from "@unhead/react"
import {Check, ChevronDown, HelpCircle, Sparkles} from 'lucide-react'
import { faqDoula, formulesMaman, images, prestations, prestationsBebe, serviceArea, siteName, travelFees } from "@/data/site"

function Services() {
  useSeoMeta({
    title: `Accompagnements · ${siteName}`,
    description: "Doula à Inguiniel (56240) : accompagnement, massages prénatal et postnatal, ateliers massage bébé.",
    ogTitle: `Accompagnements · ${siteName}`,
    ogDescription: "Doula à Inguiniel (56240) : accompagnement, massages prénatal et postnatal, ateliers massage bébé.",
    ogImage: images.hero,
    twitterCard: "summary_large_image",
  })

  return (
    <div>
      <section className="mx-auto max-w-6xl px-5 pb-8 pt-16 md:px-8">
        <p className="text-xs uppercase tracking-[0.28em] text-[#b85c38]">Accompagnements</p>
        <h1 className="mt-3 max-w-3xl font-display text-5xl leading-tight md:text-6xl">
          Des soins et un accompagnement sur-mesure
        </h1>
        <p className="mt-5 max-w-2xl text-lg text-stone-600">
          De la grossesse aux premières semaines avec bébé, chaque prestation peut se vivre seule
          ou s'inscrire dans un accompagnement plus large. Nous choisissons ensemble ce qui vous convient.
        </p>
      </section>

      <section className="mx-auto max-w-6xl space-y-10 px-5 pb-16 md:px-8">
        {prestations.map((prestation, index) => (
          <article
            key={prestation.id}
            className={`grid overflow-hidden rounded-[32px] bg-white md:grid-cols-2 ${
              index % 2 === 1 ? "md:[&>img]:order-2" : ""
            }`}
          >
            <div className="flex flex-col">
              <img
                src={prestation.image}
                alt={prestation.title}
                loading="lazy"
                className="h-72 w-full object-cover md:h-full"
              />
              <p className="p-2 text-center text-[10px] italic text-[#8c6d58]">Photo non contractuelle</p>
            </div>
            <div className="p-8 md:p-12">
              <p className="text-[11px] uppercase tracking-[0.22em] text-[#b85c38]">{prestation.kicker}</p>
              <h2 className="mt-2 font-display text-4xl">{prestation.title}</h2>
              <p className="mt-4 whitespace-pre-line text-stone-600">{prestation.summary}</p>
              <ul className="mt-6 space-y-3">
                {prestation.details.map((detail) => (
                  <li key={detail} className="flex items-start gap-3 text-sm text-stone-700">
                    <Check size={16} className="mt-0.5 shrink-0 text-[#b85c38]" />
                    {detail}
                  </li>
                ))}
              </ul>
              {"note" in prestation && prestation.note ? (
                <p className="mt-4 text-sm italic leading-relaxed text-stone-500">{prestation.note}</p>
              ) : null}
              <div className="mt-7 divide-y divide-stone-200/60 rounded-2xl bg-[#f4eee6] p-5">
                {prestation.pricing.map((tarif) => (
                  <div key={tarif.label} className="flex items-center justify-between gap-4 py-2.5 first:pt-0 last:pb-0 text-sm">
                    <span className="text-stone-700 leading-snug">{tarif.label}</span>
                    <span className="shrink-0 text-right font-display text-lg font-semibold text-[#6f4b3e] tabular-nums whitespace-nowrap">
                      {tarif.price}
                    </span>
                  </div>
                ))}
              </div>
              <Link
                to="/contact"
                className="mt-6 inline-block rounded-full bg-[#6f4b3e] px-6 py-3 text-center text-sm uppercase tracking-[0.14em] text-white transition hover:bg-[#5a3c32]"
              >
                En parler
              </Link>
            </div>
          </article>
        ))}
      </section>

      {/* Formules Maman */}
      <section className="mx-auto max-w-6xl px-5 pb-16 md:px-8">
        <div className="rounded-[32px] bg-[#fbf7f1] border border-[#e8dcd0] p-8 md:p-12">
          <div className="flex items-center gap-2">
            <Sparkles size={20} className="text-[#b85c38]" />
            <p className="text-xs uppercase tracking-[0.28em] text-[#b85c38]">Formules Massages Maman</p>
          </div>
          <h2 className="mt-2 font-display text-3xl md:text-4xl text-stone-900">
            Combinez vos soins pour un suivi personnalisé
          </h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {formulesMaman.map((formule) => (
              <div
                key={formule.id}
                className="flex flex-col justify-between rounded-2xl bg-white p-7 shadow-sm border border-stone-200/70"
              >
                <div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display text-2xl text-stone-900">{formule.title}</h3>
                      <p className="text-sm font-medium text-[#b85c38]">{formule.subtitle}</p>
                    </div>
                    <span className="font-display text-3xl font-semibold text-[#6f4b3e] tabular-nums">
                      {formule.price}
                    </span>
                  </div>
                  <p className="mt-4 text-sm text-stone-600 leading-relaxed">{formule.description}</p>
                </div>
                <div className="mt-6 pt-5 border-t border-stone-100 flex items-center justify-between">
                  <span className="text-xs font-medium uppercase tracking-wider text-stone-500">
                    {formule.savings}
                  </span>
                  <Link
                    to="/contact"
                    className="rounded-full bg-[#6f4b3e] px-4 py-2 text-xs uppercase tracking-[0.14em] text-white transition hover:bg-[#5a3c32]"
                  >
                    Réserver
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#f4eee6]">
        <div className="mx-auto max-w-6xl px-5 py-20 md:px-8">
          <p className="text-xs uppercase tracking-[0.28em] text-[#b85c38]">Prestations bébé</p>
          <h2 className="mt-2 max-w-2xl font-display text-4xl leading-tight md:text-5xl">
            Des ateliers pour tisser le lien avec votre bébé
          </h2>
          <p className="mt-4 max-w-2xl text-stone-600">
            En individuel chez vous ou en petit groupe, ces ateliers d'une heure vous donnent des clés
            simples pour apaiser, porter et éveiller votre bébé en toute confiance.
          </p>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {prestationsBebe.map((atelier) => (
              <article key={atelier.id} className="overflow-hidden rounded-[28px] bg-white">
                <img
                  src={atelier.image}
                  alt={atelier.title}
                  loading="lazy"
                  className="h-56 w-full object-cover"
                />
                <p className="px-4 pt-1.5 text-right text-[10px] italic text-[#8c6d58]">Photo non contractuelle</p>
                <div className="p-7">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-display text-2xl">{atelier.title}</h3>
                    <span className="whitespace-nowrap text-xs uppercase tracking-[0.14em] text-[#b85c38]">
                      {atelier.duration}
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-stone-600">{atelier.summary}</p>
                  <div className="mt-5 flex flex-wrap gap-2.5">
                    {atelier.pricing.map((tarif) => (
                      <div
                        key={tarif.label}
                        className="inline-flex items-center gap-2 rounded-full bg-[#efe4d8] px-4 py-1.5 text-sm"
                      >
                        <span className="text-stone-600">{tarif.label}</span>
                        <span className="font-display text-base font-semibold text-[#6f4b3e] tabular-nums">{tarif.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
          <Link
            to="/contact"
            className="mt-8 inline-block rounded-full bg-[#6f4b3e] px-6 py-3 text-center text-sm uppercase tracking-[0.14em] text-white transition hover:bg-[#5a3c32]"
          >
            Réserver un atelier
          </Link>
        </div>
      </section>

      <section className="bg-[#efe4d8]">
        <div className="mx-auto max-w-6xl px-5 py-20 md:px-8">
          <h2 className="font-display text-4xl md:text-5xl">Zone d'intervention</h2>
          <p className="mt-3 max-w-2xl text-stone-700 leading-relaxed">
            {serviceArea}
          </p>
          <p className="mt-3 max-w-2xl text-sm text-stone-600 leading-relaxed">
            {travelFees}
          </p>
        </div>
      </section>

      {/* Section FAQ */}
      <section className="bg-[#fbf7f1] py-20">
        <div className="mx-auto max-w-4xl px-5 md:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#efe4d8] px-4 py-1.5 text-xs uppercase tracking-[0.22em] text-[#b85c38]">
              <HelpCircle size={15} />
              <span>Questions fréquentes</span>
            </div>
            <h2 className="mt-4 font-display text-4xl text-stone-900 md:text-5xl">
              Foire aux questions
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-stone-600">
              Retrouvez les réponses aux questions les plus fréquentes sur l'accompagnement doula et le déroulement des séances.
            </p>
          </div>

          <div className="mt-12 space-y-4">
            {faqDoula.map((item, index) => (
              <FaqItem key={index} question={item.question} answer={item.answer} defaultOpen={index === 0} />
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-8 px-5 py-20 md:grid-cols-2 md:px-8">
        <img
          src={images.kit}
          alt="Objets doux d'accompagnement périnatal"
          loading="lazy"
          className="h-80 w-full rounded-[32px] object-cover"
        />
        <div className="flex flex-col justify-center">
          <h2 className="font-display text-4xl">Une question, un besoin particulier ?</h2>
          <p className="mt-4 text-stone-600">
            Chaque grossesse et chaque naissance sont différentes. Si votre besoin ne correspond pas
            exactement à l'une de ces prestations, parlons-en : nous adapterons l'accompagnement ensemble.
          </p>
          <p className="mt-4 text-sm text-stone-500">
            L'accompagnement d'une doula ne remplace en aucun cas le suivi médical assuré par une
            sage-femme ou un gynécologue-obstétricien.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-block w-fit rounded-full border border-[#6f4b3e] px-6 py-3 text-center text-sm uppercase tracking-[0.14em] text-[#6f4b3e] transition hover:bg-[#6f4b3e] hover:text-white"
          >
            Me contacter
          </Link>
        </div>
      </section>
    </div>
  )
}

function FaqItem({ question, answer, defaultOpen = false }: { question: string; answer: string; defaultOpen?: boolean }) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="overflow-hidden rounded-2xl border border-stone-200/80 bg-white transition-shadow hover:shadow-sm">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between gap-4 p-6 text-left transition"
        aria-expanded={isOpen}
      >
        <span className="font-display text-xl text-stone-900">{question}</span>
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f4eee6] text-[#6f4b3e] transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        >
          <ChevronDown size={18} />
        </span>
      </button>
      {isOpen ? (
        <div className="border-t border-stone-100 px-6 pb-6 pt-3 text-sm leading-relaxed text-stone-600">
          <p>{answer}</p>
        </div>
      ) : null}
    </div>
  )
}

export default Services
